import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Icon } from '@iconify/react';
import apiClient from '../../services/api/apiClient';
import { useToast } from '../../components/ui';
import OnboardingLayout from '../../components/onboarding/OnboardingLayout';

function Step1ConnectAccount() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [isConnecting, setIsConnecting] = useState(false);
  const [connectingPlatform, setConnectingPlatform] = useState(null);
  const [connectedAccounts, setConnectedAccounts] = useState([]);
  const [loading, setLoading] = useState(true);

  // 編輯模式：從 URL query 讀取帳號 ID
  const editAccountId = searchParams.get('edit');
  const isEditMode = !!editAccountId;
  const [editingAccount, setEditingAccount] = useState(null);

  useEffect(() => {
    loadConnectedAccounts();
    // 檢查是否需要觸發預先分析
    if (!isEditMode) {
      checkAndTriggerAnalysis();
    }
  }, []);

  // 編輯模式：載入帳號詳細資訊
  useEffect(() => {
    if (isEditMode && editAccountId) {
      loadEditingAccount(editAccountId);
    }
  }, [isEditMode, editAccountId]);

  const checkAndTriggerAnalysis = async () => {
    const pendingAnalysis = localStorage.getItem('onboarding_pending_analysis');
    if (pendingAnalysis === 'true') {
      // 清除標記
      localStorage.removeItem('onboarding_pending_analysis');

      // 延遲 2 秒後開始分析（確保 OAuth 完成並同步數據）
      setTimeout(async () => {
        try {
          const accountsResponse = await apiClient.get('/user/social-accounts/account-settings');

          const accounts = accountsResponse.data.data || [];
          const threadsAccount = accounts.find(acc => acc.status === 'active' && acc.platform === 'threads');

          if (threadsAccount) {
            // 觸發背景分析（不等待結果）
            triggerBackgroundAnalysis(threadsAccount.id);
          }
        } catch (error) {
          console.error('觸發預先分析失敗:', error);
        }
      }, 2000);
    }
  };

  const triggerBackgroundAnalysis = async (accountId) => {
    try {
      // 獲取貼文
      const postsResponse = await apiClient.get(`/content/posts?accountId=${accountId}&limit=50&status=published`);

      const posts = postsResponse.data.posts || [];
      const postsWithContent = posts.filter(post =>
        post.content_text && post.content_text.trim() && post.platform === 'threads'
      );

      if (postsWithContent.length >= 10) {
        // 背景調用分析 API
        const analysisResponse = await apiClient.post('/ai/analyze-account-themes', {
          accountId,
          posts: postsWithContent.slice(0, 50)
        });

        if (analysisResponse.data.success) {
          // 儲存分析結果到 localStorage
          localStorage.setItem('onboarding_theme_analysis', JSON.stringify(analysisResponse.data.themes));
          console.log('✅ 主題分析完成，已快取結果');
        }
      }
    } catch (error) {
      console.error('背景分析失敗:', error);
      // 不影響用戶流程，靜默失敗
    }
  };

  const loadConnectedAccounts = async () => {
    try {
      const response = await apiClient.get('/user/social-accounts/account-settings');

      if (response.data.success) {
        setConnectedAccounts(response.data.data.filter(acc => acc.status === 'active'));
      }
    } catch (error) {
      console.error('載入帳號失敗:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadEditingAccount = async (accountId) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/user/social-accounts/account-settings/id/${accountId}`);

      if (response.data.success) {
        setEditingAccount(response.data.data);
        console.log('編輯帳號資料:', response.data.data);
      }
    } catch (error) {
      console.error('載入帳號詳細資訊失敗:', error);
      toast.error('無法載入帳號資訊');
      navigate('/onboarding/step1');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectThreads = async () => {
    try {
      setIsConnecting(true);
      setConnectingPlatform('threads');

      const response = await apiClient.post('/user/social-accounts/pre-create', {
        platform: 'threads',
        contentType: 'mixed'
      });

      if (response.data.success) {
        const socialAccountId = response.data.accountId;

        // 標記需要預先分析（OAuth 成功後執行）
        localStorage.setItem('onboarding_pending_analysis', 'true');
        localStorage.setItem('onboarding_account_id', socialAccountId);

        // Fetch Threads OAuth URL from backend proxy
        const urlResponse = await fetch(`https://meta.viralarc.ai/api/auth/threads/url?account_id=${encodeURIComponent(socialAccountId)}`);
        if (!urlResponse.ok) {
          throw new Error('Failed to get Threads OAuth URL');
        }

        const { url } = await urlResponse.json();
        if (!url) {
          throw new Error('Missing OAuth URL');
        }

        window.location.href = url;
      }
    } catch (error) {
      console.error('連接 Threads 帳號時發生錯誤:', error);
      toast.error('連接失敗，請稍後再試');
    } finally {
      setIsConnecting(false);
      setConnectingPlatform(null);
    }
  };

  const handleNext = () => {
    // 標記 Step 1 完成（即使沒連接帳號也算完成）
    localStorage.setItem('onboarding_step1_completed', 'true');

    // 編輯模式：帶上帳號 ID 到下一步
    if (isEditMode && editAccountId) {
      localStorage.setItem('editing_account_id', editAccountId);
      navigate(`/onboarding/step2?edit=${editAccountId}`);
    } else {
      navigate('/onboarding/step2');
    }
  };

  const handleSkip = () => {
    // 標記 Step 1 完成（跳過也算完成）
    localStorage.setItem('onboarding_step1_completed', 'true');
    navigate('/onboarding/step2');
  };

  const accountOptions = [
    {
      id: 'threads',
      title: 'Threads',
      description: '連接您的 Threads 帳號，開始自動化發文與數據分析',
      icon: 'simple-icons:threads',
      iconBgColor: 'bg-gray-900',
      iconColor: 'text-white',
      available: true,
      onClick: handleConnectThreads
    },
    {
      id: 'instagram',
      title: 'Instagram',
      description: '連接 Instagram 商業帳號，管理貼文與限時動態',
      icon: 'mdi:instagram',
      iconBgColor: 'bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400',
      iconColor: 'text-white',
      available: false,
      onClick: null
    },
    {
      id: 'facebook',
      title: 'Facebook',
      description: '連接 Facebook 粉絲專頁，統一管理社群內容',
      icon: 'mdi:facebook',
      iconBgColor: 'bg-blue-600',
      iconColor: 'text-white',
      available: false,
      onClick: null
    }
  ];

  const illustration = (
    <div className="w-full max-w-md mx-auto">
      <div className="relative flex items-center justify-center">
        {/* Social connection visualization */}
        <div className="relative">
          {/* Main container */}
          <div className="w-64 h-64 rounded-full bg-white/60 backdrop-blur-sm shadow-lg flex items-center justify-center">
            {/* Center node */}
            <div className="relative w-40 h-40 rounded-full bg-blue-500 shadow-lg flex items-center justify-center">
              <Icon icon="mdi:account-circle" className="text-6xl text-white" />
            </div>
          </div>

          {/* Floating platform icons */}
          <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-16 h-16 rounded-2xl bg-gray-900 shadow-lg flex items-center justify-center">
            <Icon icon="simple-icons:threads" className="text-2xl text-white" />
          </div>

          <div className="absolute top-1/2 -translate-y-1/2 -right-4 w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 shadow-lg flex items-center justify-center opacity-40">
            <Icon icon="mdi:instagram" className="text-xl text-white" />
          </div>

          <div className="absolute top-1/2 -translate-y-1/2 -left-4 w-14 h-14 rounded-2xl bg-blue-600 shadow-lg flex items-center justify-center opacity-40">
            <Icon icon="mdi:facebook" className="text-xl text-white" />
          </div>

          {/* Connection lines */}
          <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ transform: 'translate(-10%, -10%)' }}>
            <line x1="50%" y1="20%" x2="50%" y2="50%" stroke="#60A5FA" strokeWidth="2" strokeDasharray="4 4" />
          </svg>
        </div>
      </div>
    </div>
  );

  return (
    <OnboardingLayout
      currentStep={1}
      totalSteps={6}
      title={isEditMode ? "編輯帳號設定" : "連接你的社群帳號"}
      illustration={illustration}
      showBackButton={isEditMode}
      onBackClick={isEditMode ? () => navigate(-1) : undefined}
      progressColor="#60A5FA"
    >
      {/* Instructions */}
      {!isEditMode && (
        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <Icon icon="mdi:information-outline" className="text-blue-500 text-xl flex-shrink-0 mt-0.5" />
            <div className="text-sm text-gray-700">
              <p className="font-medium mb-1">開始使用 ViralArc</p>
              <p className="text-gray-600">
                連接您的社群媒體帳號，即可開始使用 AI 自動化內容生成與排程發布功能。您可以隨時在設定中管理帳號。
              </p>
            </div>
          </div>
        </div>
      )}

      {/* 編輯模式：顯示正在編輯的帳號 */}
      {isEditMode && editingAccount && (
        <div className="mb-6 p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl">
          <div className="flex items-center gap-2 mb-4">
            <Icon icon="mdi:pencil-outline" className="text-xl text-blue-600" />
            <h3 className="font-semibold text-gray-900">正在編輯的帳號</h3>
          </div>
          <div className="flex items-center gap-4 p-4 bg-white rounded-lg shadow-sm">
            <div className="w-14 h-14 bg-gray-900 rounded-full flex items-center justify-center">
              <Icon icon="simple-icons:threads" className="text-2xl text-white" />
            </div>
            <div className="flex-1">
              <div className="font-semibold text-lg text-gray-900">@{editingAccount.account_name}</div>
              <div className="text-sm text-gray-500 capitalize">{editingAccount.platform}</div>
              <div className="flex items-center gap-2 mt-1">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  editingAccount.status === 'active'
                    ? 'bg-green-100 text-green-700'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {editingAccount.status === 'active' ? '已連接' : editingAccount.status}
                </span>
                {editingAccount.platform_account_id && (
                  <span className="text-xs text-gray-400">
                    ID: {editingAccount.platform_account_id}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Connected Accounts - 僅在非編輯模式顯示 */}
      {!isEditMode && connectedAccounts.length > 0 && (
        <div className="mb-6 p-4 bg-green-50 border border-green-100 rounded-xl">
          <div className="flex items-center gap-2 mb-3">
            <Icon icon="mdi:check-circle" className="text-xl text-green-500" />
            <h3 className="font-semibold text-gray-900">已連接的帳號</h3>
          </div>
          <div className="space-y-2">
            {connectedAccounts.map(account => (
              <div key={account.id} className="flex items-center gap-3 p-3 bg-white rounded-lg">
                <div className="w-10 h-10 bg-gray-900 rounded-full flex items-center justify-center">
                  <Icon icon="simple-icons:threads" className="text-lg text-white" />
                </div>
                <div className="flex-1">
                  <div className="font-medium text-gray-900">@{account.account_name}</div>
                  <div className="text-xs text-gray-500">{account.platform}</div>
                </div>
                <Icon icon="mdi:check-circle" className="text-lg text-green-500" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Account Options - 僅在非編輯模式顯示 */}
      {!isEditMode && (
        <div className="space-y-4 mb-6">
        {accountOptions.map((option) => {
          const isActive = option.available;
          const isLoading = isConnecting && connectingPlatform === option.id;

          return (
            <button
              key={option.id}
              onClick={isActive ? option.onClick : undefined}
              disabled={!isActive || isLoading}
              className={`
                w-full p-6 rounded-xl border-2 transition-all text-left
                ${isActive
                  ? 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                  : 'border-gray-100 bg-gray-50 opacity-60 cursor-not-allowed'
                }
              `}
            >
              <div className="flex items-center gap-4">
                <div className={`w-12 h-12 rounded-xl ${option.iconBgColor} flex items-center justify-center flex-shrink-0`}>
                  <Icon icon={option.icon} className={`text-2xl ${option.iconColor}`} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-semibold text-gray-900">
                      {option.title}
                    </h3>
                    {!isActive && (
                      <span className="text-xs px-2 py-0.5 bg-gray-200 text-gray-500 rounded-full font-medium">
                        即將推出
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600">
                    {option.description}
                  </p>
                </div>
                {isLoading ? (
                  <Icon icon="line-md:loading-loop" className="text-2xl text-blue-500 flex-shrink-0" />
                ) : isActive ? (
                  <Icon icon="mdi:arrow-right" className="text-2xl text-gray-400 flex-shrink-0" />
                ) : null}
              </div>
            </button>
          );
        })}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-between mt-8">
        {isEditMode ? (
          <>
            <button
              onClick={() => navigate(-1)}
              className="px-6 py-2.5 text-gray-600 font-medium rounded-xl hover:bg-gray-100 transition-colors flex items-center gap-2"
            >
              <Icon icon="mdi:arrow-left" className="text-xl" />
              取消
            </button>
            <button
              onClick={handleNext}
              disabled={loading}
              className="px-8 py-3 bg-blue-500 text-white font-medium rounded-xl hover:bg-blue-600 transition-colors flex items-center gap-2"
            >
              下一步
              <Icon icon="mdi:arrow-right" className="text-xl" />
            </button>
          </>
        ) : (
          <>
            <button
              onClick={handleSkip}
              className="px-6 py-2.5 text-gray-600 font-medium rounded-xl hover:bg-gray-100 transition-colors"
            >
              跳過此步驟
            </button>
            <button
              onClick={handleNext}
              disabled={loading || isConnecting}
              className={`
                px-8 py-3 font-medium rounded-xl transition-colors flex items-center gap-2
                ${connectedAccounts.length > 0 || !loading
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                }
              `}
            >
              {connectedAccounts.length > 0 ? '繼續設定' : 'Next'}
              <Icon icon="mdi:arrow-right" className="text-xl" />
            </button>
          </>
        )}
      </div>
    </OnboardingLayout>
  );
}

export default Step1ConnectAccount;
